import { AssignedParticipantUnreadInfo, ConversationView } from '@mark43/cad-api';
import { ParticipantIdentities } from '../store/types';
import { getRecordKeys } from '@m43/core';
import { isActiveConversation } from '../store/helpers';
import { AlertContext, audibleAlerts } from '../../sounds/audible-alerts';

export interface UnreadUrgencies {
    hasUrgentUnread: boolean;
    hasStandardUnread: boolean;
}

const NO_UNREADS: UnreadUrgencies = { hasUrgentUnread: false, hasStandardUnread: false };

export const getParticipantUnreadUrgencies = ({
    unreadCount,
    hasUrgentUnread,
    hasStandardUnread,
}: AssignedParticipantUnreadInfo): UnreadUrgencies =>
    unreadCount === 0 ? NO_UNREADS : { hasUrgentUnread, hasStandardUnread };

/**
 * The urgencies of the unread messages in the user's active conversations. Nothing is unread on
 * the first load, before there are previous conversations to compare against.
 */
export const getUnreadUrgenciesForConversations = ({
    previousConversations,
    currentConversations,
    participantIdentities,
}: {
    previousConversations?: Record<number, ConversationView>;
    currentConversations?: Record<number, ConversationView>;
    participantIdentities?: ParticipantIdentities;
}): UnreadUrgencies => {
    if (!previousConversations || !currentConversations || !participantIdentities) {
        return NO_UNREADS;
    }
    let hasUrgentUnread = false;
    let hasStandardUnread = false;
    getRecordKeys(currentConversations).forEach((id) => {
        const conversation = currentConversations[id];
        if (!isActiveConversation(conversation, participantIdentities)) {
            return;
        }
        conversation.assignedParticipantUnreadInfos.forEach((participant) => {
            const urgencies = getParticipantUnreadUrgencies(participant);
            hasUrgentUnread = hasUrgentUnread || urgencies.hasUrgentUnread;
            hasStandardUnread = hasStandardUnread || urgencies.hasStandardUnread;
        });
    });
    return { hasUrgentUnread, hasStandardUnread };
};

/**
 * Plays the new message alert for the most urgent unread that has a sound: the urgent alert, or the
 * standard alert when there is no urgent unread or the urgent alert is silent.
 */
export const playNewMessageAlert = (
    { hasUrgentUnread, hasStandardUnread }: UnreadUrgencies,
    context: AlertContext
): void => {
    if (
        hasUrgentUnread &&
        audibleAlerts.playAlert({ type: 'MESSAGE_RECEIVED', context, isUrgent: true })
    ) {
        return;
    }
    if (hasStandardUnread) {
        audibleAlerts.playAlert({ type: 'MESSAGE_RECEIVED', context, isUrgent: false });
    }
};
